import type {SketchSolverSnapshotSupplementProvider} from '../solver/constraint/solverHistory/SketchSolverRuntimeTypes';
import type {SketchDimensionState} from '../../history/solver/SketchSolverRecordTypes';
import type {SketchDimension} from './model/SketchDimension';
import type {SketchDimensionSnapshot} from './SketchDimensionTypes';
import type {SketchDimensionRepository} from './repository/SketchDimensionRepository';

/**
 * 把尺寸聚合映射为 solver island 尺寸状态 DTO。
 *
 * @param dimension 尺寸聚合。
 * @returns solver 尺寸状态。
 */
function toSolverDimensionState(snapshot: SketchDimensionSnapshot): SketchDimensionState {
    const targetEntityIds = [...new Set(snapshot.references.map((reference) => reference.entityId))];
    const targetPointIds = [...new Set(
        snapshot.references
            .map((reference) => reference.pointId)
            .filter((pointId): pointId is string => Boolean(pointId)),
    )];

    return {
        dimensionId: snapshot.dimensionId,
        dimensionKind: snapshot.dimensionKind,
        drivingState: snapshot.role,
        value: snapshot.parameter.nominalValue,
        ...(snapshot.measuredValue === null ? {} : {measuredValue: snapshot.measuredValue}),
        unit: snapshot.parameter.quantityKind === 'angle' ? 'rad' : 'm',
        targetEntityIds,
        targetPointIds,
        ...(snapshot.suppressed ? {suppressed: true} : {}),
        ...(snapshot.solveState === 'dangling' ? {dangling: true} : {}),
        ...(snapshot.solveState === 'conflicting' ? {conflicting: true} : {}),
        parameterId: snapshot.parameter.parameterId,
    };
}

/**
 * 从完整尺寸快照创建 solver snapshot 补充 provider。
 *
 * 该入口供非尺寸命令触发的全 island 重算复用，确保外部引用重建、历史恢复等路径
 * 与尺寸命令使用同一份尺寸状态映射，不另造尺寸关系旁路。
 *
 * @param dimensions 同一草图中的完整尺寸快照。
 * @returns 只读尺寸状态 provider。
 */
export function createSketchDimensionSolverSupplementProviderFromSnapshots(
    dimensions: readonly SketchDimensionSnapshot[],
): SketchSolverSnapshotSupplementProvider {
    const dimensionStates = dimensions.map(toSolverDimensionState);

    return {
        readSketchSolverSnapshotSupplement() {
            return dimensionStates.length > 0 ? {dimensionStates} : {};
        },
    };
}

/**
 * 创建从尺寸仓库读取 solver snapshot 补充状态的 provider。
 *
 * 设计说明：
 * 1. 该 provider 注入 `configureSketchSolverHistoryRuntime`，使 solver island
 *    快照的 `dimensionStates` 不再恒为空（02 计划 §8.3 缺口收口）；
 * 2. 只输出可序列化尺寸主状态；尺寸分支保存在 dimension state，关系分支由关系快照独立采集；
 * 3. 输出按 dimensionId 字典序（仓库 listBySketch 保证），确保 island 快照
 *    语义比较稳定；
 * 4. 身份桥接：island 采集的 `input.sketchId` 是草图运行时 uuid，而仓库按
 *    持久 ID 分桶；宿主必须注入 `resolveSketchPersistentId` 完成归一化
 *    （直接命中持久 ID 的输入则原样使用）。
 *
 * @param repository 尺寸仓库。
 * @param resolveSketchPersistentId 运行时草图 ID → 持久 ID 解析；无法解析返回 null。
 * @returns solver snapshot 补充状态 provider。
 */
export function createSketchDimensionSolverSupplementProvider(
    repository: SketchDimensionRepository,
    resolveSketchPersistentId?: (runtimeSketchId: string) => string | null,
): SketchSolverSnapshotSupplementProvider {
    return {
        readSketchSolverSnapshotSupplement(input) {
            const persistentSketchId = repository.listBySketch(input.sketchId).length > 0
                ? input.sketchId
                : resolveSketchPersistentId?.(input.sketchId) ?? input.sketchId;
            const dimensionStates = repository
                .listBySketch(persistentSketchId)
                .map((dimension: SketchDimension) => toSolverDimensionState(dimension.toSnapshot()));

            return dimensionStates.length > 0 ? {dimensionStates} : {};
        },
    };
}
